import { FC, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';

import { FormSectionCard } from 'components/_basic/FormSectionCard';

import { PROJECTED_CASH_FLOW_OPTION_NAMES, selectedTabPrefix } from '../../../ProjectedCashFlow/utils/form';
import { useCalculateTotal } from '../../hooks/useCalculateTotal';
import { omitFieldsForTotalAssetsSum } from '../../InitialAssetsForm/utils/form';
import { omitFieldsForTotalEquitySum } from '../../InitialEquityForm/utils/form';
import { omitFieldsForTotalLiabilitiesSum } from '../../InitialLiabilitiesForm/utils/form';
import {
  initialAssetsFormPrefix,
  initialEquityFormPrefix,
  initialLiabilitiesFormPrefix,
  isEqualFieldName,
} from '../../utils/form';
import { BalanceAlert } from '../BalanceAlert/BalanceAlert';
import { BalanceMessage } from '../BalanceMessage/BalanceMessage';
import { TotalComponent } from '../TotalComponent/TotalComponent';

import styles from './BalanceSheet.module.scss';

export const BalanceSheet: FC = () => {
  const { t } = useTranslation();
  const { setValue } = useFormContext();

  const totalAssetsFieldValues = useWatch({
    name: initialAssetsFormPrefix,
  });

  const totalLiabilitiesFieldValues = useWatch({
    name: initialLiabilitiesFormPrefix,
  });

  const totalEquityFieldValues = useWatch({
    name: initialEquityFormPrefix,
  });

  const totalAssets = useCalculateTotal(totalAssetsFieldValues, omitFieldsForTotalAssetsSum);
  const totalLiabilities = useCalculateTotal(totalLiabilitiesFieldValues, omitFieldsForTotalLiabilitiesSum);
  const totalEquity = useCalculateTotal(totalEquityFieldValues, omitFieldsForTotalEquitySum);

  const isMatch = totalAssets === totalLiabilities + totalEquity;

  useEffect(() => {
    setValue(isEqualFieldName, isMatch);
    if (!isMatch) {
      setValue(selectedTabPrefix, PROJECTED_CASH_FLOW_OPTION_NAMES.DIRECT);
    }
  }, [isMatch]);

  return (
    <FormSectionCard className={styles.container}>
      <>
        <Box className={styles.totalContainer}>
          <TotalComponent
            total={totalAssets}
            error={!isMatch}
            title={t('financialPlan.openingBalanceSheets.totalAssets')}
          />
          <Typography variant="subtitle1" className={styles.arithmeticOperator}>
            =
          </Typography>
          <TotalComponent
            total={totalLiabilities}
            error={!isMatch}
            title={t('financialPlan.openingBalanceSheets.totalLiabilities')}
          />
          <Typography variant="subtitle1" className={styles.arithmeticOperator}>
            +
          </Typography>
          <TotalComponent
            total={totalEquity}
            error={!isMatch}
            title={t('financialPlan.openingBalanceSheets.totalEquity')}
          />
        </Box>

        {!isMatch ? <BalanceAlert /> : <BalanceMessage />}
      </>
    </FormSectionCard>
  );
};
